import { useRef, useState } from "react";
import { toast } from "react-toastify";

import { notifyPageAttachmentsChanged } from "../../Utils/pageAttachmentEvents";
import DocumentUploadModal from "../Modals/documentUploadModal";

import { uploadCaseFiles } from "./uploads/upload-case-files";

const CaseUploadButton = ({ pageId = null, pageName, onUploaded }) => {
  const inputRef = useRef(null);
  const uploadInProgress = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadStates, setUploadStates] = useState([]);
  const isPageUpload = pageId !== null && pageId !== "all" && pageId !== "";
  const destination = isPageUpload ? `${pageName || "this"} Page` : "case as unsorted";
  const progress = uploadStates.length
    ? Math.floor(
        uploadStates.reduce((total, state) => total + state.progress, 0) / uploadStates.length
      )
    : 0;

  const saveFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || uploadInProgress.current) return;
    uploadInProgress.current = true;
    setIsUploading(true);
    setUploadFiles(files.map((file) => file.name));
    setUploadStates(files.map(() => ({ status: "queued", progress: 0 })));
    setShowProgress(true);
    try {
      const result = await uploadCaseFiles(files, {
        pageId,
        onFileProgress: (index, update) => {
          setUploadStates((current) =>
            current.map((state, i) => (i === index ? { ...state, ...update } : state))
          );
        },
      });
      if (result.failed)
        toast.error(`${result.failed} file(s) could not be saved. Please try again.`);
      if (result.documents) notifyPageAttachmentsChanged(result.caseId, result.pageId, "docs");
      if (result.photos) notifyPageAttachmentsChanged(result.caseId, result.pageId, "photos");
      if (result.uploaded) await onUploaded?.(result);
    } finally {
      uploadInProgress.current = false;
      setIsUploading(false);
    }
  };

  return (
    <>
      <div
        className="d-flex align-items-center"
        style={{ height: 25 }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          return saveFiles(event.dataTransfer.files);
        }}
      >
        <button
          type="button"
          className="btn btn-success text-white d-flex align-items-center justify-content-center"
          style={{ height: 21, padding: "0 8px", gap: 5, whiteSpace: "nowrap" }}
          disabled={isUploading}
          aria-busy={isUploading}
          aria-label={`Save photos and documents to ${destination}`}
          onClick={() => inputRef.current?.click()}
        >
          <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M11 16h2V7.83l3.59 3.58L18 10l-6-6-6 6 1.41 1.41L11 7.83V16zM5 18v2h14v-2h2v4H3v-4h2z" />
          </svg>
          {isPageUpload ? "Save to Page" : "Save to Case"}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        aria-label={`Choose photos and documents to save to ${destination}`}
        style={{ display: "none" }}
        onChange={async (event) => {
          const input = event.currentTarget;
          try {
            await saveFiles(input.files);
          } finally {
            input.value = "";
          }
        }}
      />
      <DocumentUploadModal
        show={showProgress}
        uploadFile={uploadFiles}
        uploadProgress={progress}
        uploadStates={uploadStates}
        progressTitle="File Upload Progress:"
        progressDescription={`Saving photos and documents to ${destination}.`}
        onHide={() => setShowProgress(false)}
      />
    </>
  );
};

export default CaseUploadButton;
